import { ApolloClient } from '@apollo/client'
import { useEffect, useState } from 'react'
import CryptoJS from 'crypto-js'
import { envVar } from '@babylon/babylon-env'
import { GET_CONSULTANTS, Consultant, GetConsultantsResponse } from '../queries'

const STORAGE_KEY = 'app_cache'
const SECRET_KEY = envVar('CLINICIAN_CACHE_ENCRYPTION_KEY')

/** Encrypt and persist consultants to sessionStorage */
const saveToStorage = (consultants: Consultant[]): void => {
  try {
    const json = JSON.stringify(consultants)
    const encrypted = CryptoJS.AES.encrypt(json, SECRET_KEY).toString()
    sessionStorage.setItem(STORAGE_KEY, encrypted)
  } catch {
    // sessionStorage may be full or unavailable — silently ignore
  }
}

/** Read and decrypt consultants from sessionStorage */
const readFromStorage = (): Consultant[] | null => {
  try {
    const encrypted = sessionStorage.getItem(STORAGE_KEY)
    if (!encrypted) return null
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY)
    const json = bytes.toString(CryptoJS.enc.Utf8)
    if (!json) return null
    return JSON.parse(json) as Consultant[]
  } catch {
    return null
  }
}

let fetchPromise: Promise<Consultant[]> | null = null
let fetchComplete = false
let fetchError: Error | undefined
/** In-memory cache so we don't decrypt on every render */
let consultantsCache: Consultant[] | null = null

const fetchPage = (
  client: ApolloClient<object>,
  page: number
): Promise<Consultant[]> =>
  client
    .query<GetConsultantsResponse>({
      query: GET_CONSULTANTS,
      variables: { filters: { id: null, full_name: null, page } },
      fetchPolicy: 'network-only',
    })
    .then(({ data }) => {
      const consultants = data?.getConsultants ?? []
      if (consultants.length === 0) return []
      return fetchPage(client, page + 1).then((next) => [
        ...consultants,
        ...next,
      ])
    })

const fetchAllPages = (client: ApolloClient<object>): Promise<Consultant[]> =>
  fetchPage(client, 1).then((allConsultants) => {
    saveToStorage(allConsultants)
    consultantsCache = allConsultants
    return allConsultants
  })

/**
 * Kick off the consultants fetch early (e.g. right after login).
 * Safe to call multiple times — only the first call triggers a network request.
 */
export const prefetchConsultants = (client: ApolloClient<object>): void => {
  if (fetchPromise !== null) return

  // Already in memory — nothing to do
  if (consultantsCache?.length) {
    fetchComplete = true
    return
  }

  // Check if already in sessionStorage
  const cached = readFromStorage()
  if (cached?.length) {
    consultantsCache = cached
    fetchComplete = true
    return
  }

  fetchError = undefined
  fetchComplete = false

  fetchPromise = fetchAllPages(client)
    .then((result) => {
      fetchComplete = true
      return result
    })
    .catch((e) => {
      fetchError = e instanceof Error ? e : new Error(String(e))
      fetchComplete = true
      fetchPromise = null
      return []
    })
}

interface UseGetConsultantsResult {
  data: Consultant[]
  loading: boolean
  error: Error | undefined
}

/**
 * Reads consultants from encrypted sessionStorage (populated by prefetchConsultants).
 * Does NOT fetch — prefetchConsultants must be called beforehand (e.g. in AppContent).
 */
export const useGetConsultants = (): UseGetConsultantsResult => {
  const [, rerender] = useState(0)

  // On first render, hydrate from sessionStorage if not already in memory
  if (!consultantsCache) {
    consultantsCache = readFromStorage()
  }

  const hasCachedData = !!consultantsCache?.length

  // If prefetch is still in-flight, wait for it and trigger a re-render
  useEffect(() => {
    if (hasCachedData || fetchComplete || !fetchPromise) return
    fetchPromise.then(() => rerender((n) => n + 1)).catch(() => {})
  }, [hasCachedData])

  return {
    data: consultantsCache ?? [],
    loading: !hasCachedData && !fetchComplete && fetchPromise !== null,
    error: fetchError,
  }
}

/** @internal — only for tests */
export const _resetForTests = () => {
  fetchPromise = null
  fetchComplete = false
  fetchError = undefined
  consultantsCache = null
  sessionStorage.removeItem(STORAGE_KEY)
}
